import { yupResolver } from '@hookform/resolvers/yup'
import { Close } from '@mui/icons-material'
import {
  CardActions,
  CardContent,
  Divider,
  Grid,
  Typography
} from '@mui/material'
import {
  Button,
  Card,
  CardHeader,
  DatePickerC,
  IconButton,
  Modal
} from 'components'
import { useLoadOrderProposal } from 'modules/orders/hooks'
import { FormProvider, useForm } from 'react-hook-form'
import { formatDate, getTimezonedIsoString, Yup } from 'utils'
import { updateDeliveryDateStore } from './updateDeliveryDateStore'
import { useUpdateOrderDeliveryDate } from './useUpdateOrderDeliveryDate'
import { useEffect } from 'react'

type FormData = {
  deliveryDate: string | null
}

const schema = Yup.object({
  deliveryDate: Yup.string().required()
})

function UpdateDeliveryDateModal() {
  const isOpen = updateDeliveryDateStore.useIsModalOpen()
  const setIsOpenModal = updateDeliveryDateStore.useSetModalIsOpen()
  const proposalId = updateDeliveryDateStore.useProposalId()

  const methods = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      deliveryDate: null
    }
  })

  const { handleSubmit, reset } = methods

  const { mutateAsync, isLoading } = useUpdateOrderDeliveryDate()
  const { data } = useLoadOrderProposal(proposalId)

  useEffect(() => {
    if (data?.deliveryDate) {
      reset({
        deliveryDate: getTimezonedIsoString(data.deliveryDate)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.deliveryDate, reset])

  const onClose = () => {
    setIsOpenModal(false)
  }

  const onSubmit = async (data: FormData) => {
    try {
      if (!data?.deliveryDate) return
      const normalizedData = {
        deliveryDate: new Date(data.deliveryDate).toISOString(),
        proposalId: proposalId
      }
      await mutateAsync(normalizedData)
      setIsOpenModal(false)
      reset()
    } catch {}
  }

  return (
    <FormProvider {...methods}>
      <Modal
        open={isOpen}
        onClose={onClose}
        keepMounted={false}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      >
        <Card style={{ padding: 0, minWidth: 300, width: 600 }}>
          <CardHeader
            title={`ATUALIZAR DATA DE ENTREGA: ${proposalId || ''}`}
            HeaderRight={
              <IconButton onClick={() => onClose()}>
                <Close />
              </IconButton>
            }
          />
          <Divider />

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography>
                  {!data?.deliveryDate
                    ? 'Nenhuma data definida ainda!'
                    : 'Data atual: ' + formatDate(data?.deliveryDate)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <DatePickerC
                  name="deliveryDate"
                  label={`Previsão de Entrega`}
                  inputFormat="dd/MM/yyyy"
                />
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <CardActions>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '1rem'
              }}
            >
              <Button
                variant="text"
                sx={{
                  width: { sm: 100, md: 200 }
                }}
                onClick={() => onClose()}
              >
                Cancelar
              </Button>
              <Button
                sx={{
                  width: { sm: 100, md: 200 }
                }}
                type="button"
                disabled={isLoading}
                onClick={handleSubmit(onSubmit)}
              >
                Atualizar
              </Button>
            </div>
          </CardActions>
        </Card>
      </Modal>
    </FormProvider>
  )
}

export default UpdateDeliveryDateModal
